/**
 * Service Worker and PWA Install Prompt Handler
 * Bundled securely without requiring inline scripts in index.html.
 * In development or inside preview iframes (such as Google AI Studio preview),
 * it ensures any stale workers are unregistered and avoids breaking Vite HMR.
 */

declare global {
  interface Window {
    __deferredPwaPrompt: any;
  }
}

export function registerAppServiceWorker() {
  if (typeof window === 'undefined') return;

  try {
    // Global early capture of PWA install prompt
    window.__deferredPwaPrompt = null;
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      window.__deferredPwaPrompt = e;
      window.dispatchEvent(new CustomEvent('pwa-prompt-ready', { detail: e }));
    });

    // Detect if running inside an iframe (like Google AI Studio preview)
    let isInIframe = false;
    try {
      isInIframe = window.self !== window.top;
    } catch {
      isInIframe = true;
    }

    if ('serviceWorker' in navigator) {
      // In dev mode OR in preview iframe: always unregister service workers to avoid stale cache or blank screens
      if (import.meta.env.DEV || isInIframe) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const reg of registrations) {
            reg.unregister().catch(() => {});
          }
        }).catch(() => {});
        return;
      }

      // Register service worker strictly in standalone / top-level production window
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((reg) => {
            reg.update().catch(() => {});
            window.addEventListener('focus', () => {
              reg.update().catch(() => {});
            });
            document.addEventListener('visibilitychange', () => {
              if (document.visibilityState === 'visible') {
                reg.update().catch(() => {});
              }
            });
          })
          .catch(() => {});
      });

      let isReloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!isReloading) {
          isReloading = true;
          window.location.reload();
        }
      });
    }
  } catch (err) {
    console.warn('[SW Registration bypassed cleanly]:', err);
  }
}

// Automatically invoke upon import
registerAppServiceWorker();
